import { atom } from "jotai";
import React from "react";

import * as u from "./utility";

import store from "store2";

import { DummyStory, StoryItem } from "./StoryItem";
import { atomWithLocation } from "jotai-location";
import { ThemeMode } from "./themeConfig";

function getCurrentProject(): string {
  return (
    u.projectFromLocation() ||
    (window.location.pathname === "/"
      ? store.get("ProjectPicker:" + window.location.search, "") || ""
      : "")
  );
}

export const projectPickerState = atom<string>(getCurrentProject());

function createCachedAtom<T>(name: string, defaultValue: T) {
  const plainAtom = atom<T>();

  return atom(
    (get): T => {
      const proj: string = get(projectPickerState);
      const key = u.generateProjectCacheKey(name, proj);
      if (
        typeof defaultValue === "string" ||
        typeof defaultValue === "number" ||
        typeof defaultValue === "boolean"
      ) {
        const cached = store.get(key, defaultValue) as T;
        const prev = get(plainAtom);
        return prev === undefined || prev !== cached ? cached : prev;
      } else {
        const v = store.get(key, defaultValue);
        const cached = v as T;
        const prev = get(plainAtom);
        return prev === undefined || prev !== cached ? cached : prev;
      }
    },
    (
      get,
      set,
      newValueOrFunc: T | ((prev: T | undefined) => T | undefined)
    ) => {
      const proj = get(projectPickerState);
      const key = u.generateProjectCacheKey(name, proj);
      if (typeof newValueOrFunc === "function") {
        const newValue = (newValueOrFunc as any)(get(plainAtom));
        store(key, newValue);
        set(plainAtom, newValue);
      } else {
        store(key, newValueOrFunc);
        set(plainAtom, newValueOrFunc);
      }
    }
  );
}

export const debugToggleState = createCachedAtom<boolean>("DebugToggle", true);

export const negateLabelsState = createCachedAtom<boolean>(
  "NegateLabels",
  false
);

export const labelToggleState = createCachedAtom<boolean>("LabelToggle", true);

export const imageToggleAtom = createCachedAtom<boolean>("ImageToggle", true);

export const hightlightToggleAtom = atom<boolean>(false);

export const timeFilterState = createCachedAtom<string>(
  "TimeFilter",
  "all time"
);

const readAllProjectCaptions = (): {
  [key: string]: { [key: string]: string };
} => {
  return JSON.parse(store.get("Captions", "{}"));
};
export const captionsState = atom<{ [key: string]: { [key: string]: string } }>(
  readAllProjectCaptions()
);

export type AccountInfo = {
  url?: string;
  avatar?: string;
  app: string;
  name?: string;
  username?: string;
  email?: string;
  id?: string;
  group?: string;
};

export const peopleDataState = atom<Map<string, AccountInfo[]>>(new Map());

export const selectedAccountsState = atom<AccountInfo[][]>([]);

export const reloadPeopleData = atom<number>(0);

export const searchState = atom<string | AccountInfo[][]>("");

export const newStoriesState = atom<(StoryItem | DummyStory)[]>([]);

export const mergedStoriesState = atom<string[]>([]);

export const reloadState = atom<number>(0);

export const showSelectedStoriesState = atom<boolean>(false);

export const displayedStoryCountState = atom<number>(0);

// Theme mode with localStorage persistence
const getStoredThemeMode = (): ThemeMode => {
  const stored = store.get('themeMode', 'system');
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
};

const themeModeInternal = atom<ThemeMode>(getStoredThemeMode());

export const themeModeState = atom(
  (get) => get(themeModeInternal),
  (get, set, newMode: ThemeMode) => {
    store.set('themeMode', newMode);
    set(themeModeInternal, newMode);
  }
);

export const menuCategoriesList = [
  "Stories",
  "Popular",
  "Recent",
  "Events",
  "Graph",
  "Artifacts",
  "Deleted",
  "People",
  "Vitals",
  "Settings",
  "Community",
] as const;

export type MenuCategory = (typeof menuCategoriesList)[number];
export const selectedCategory = atom<MenuCategory>("Stories");

export const storyFilterBoxState = atom<{ [key: string]: string[] }>();

export const activeLabelsState = atom<{
  [key: string]: {
    name?: string;
    display?: string | { icon: string };
    index: number;
    expression?: string;
    accounts?: {
      app: "topstorie" | "slack" | "github" | "gitlab";
      group?: string;
      id: string;
      email?: string;
      avatar?: string;
    }[];
  };
}>();

export const locationAtom = atomWithLocation();

export const stageAtom = atom(
  (get) => get(locationAtom).searchParams?.get("stage") || "prod2"
);

export const peopleIndexState = atom((get) => {
  const data = get(peopleDataState);
  const out = new Map<string, AccountInfo[]>();
  for (const [k, v] of data) {
    data.set(k, v);
    out.set(k, v);
    for (const acc of v) {
      if (acc.app === "github" && acc.username) {
        out.set(`${acc.app}:${acc.username}`, v);
      }
    }
  }
  return out;
});

export const joinStoriesState = createCachedAtom<{ [key: string]: boolean }>(
  "JoinStories",
  {}
);

export const appBarCustomContentState = atom<React.ReactNode>(null);
export const appBarSearchContentState = atom<React.ReactNode>(null);

// Graph-specific atoms
export type GraphData = {
  edges: [string, string][];
  nodes: { [storyId: string]: [number, string] };
};
export const graphDataState = atom<GraphData | undefined>(undefined);
export const graphSearchSelectionState = atom<string>("");
